/**
 * Line Messages UI Component
 * 
 * This component displays all "line" level messages from window.alertData
 * in a collapsible container above the legend
 */

import { iconFont } from './icon-fonts.js';

/**
 * Generate line messages section above the legend
 */
function generateLineMessages() {
    const container = document.getElementById('lineMessagesContainer');
    if (!container) return;

    // Collect all line messages from window.alertData
    const lineMessages = [];

    // Helper function to get line color
    const getLineColor = (lineKey, message) => {
        // For regularBus, use a bus-specific color

        // If the message has a color property, use it (for surface routes)
        if (message && message.color) {
            return message.color;
        }
        // Otherwise try to get from stationData
        if (window.stationData && window.stationData[lineKey] && window.stationData[lineKey].color) {
            return window.stationData[lineKey].color;
        }

        // Fallback colors
        const fallbackColors = {
            "line1": "#FFD320", // Yellow
            "line1University": "#FFD320", // Yellow
            "line1Yonge": "#FFD320", // Yellow
            "line2": "#00B04F", // Green
            "line3": "#00a4e3", // Blue
            "line4": "#a21a68", // Purple
            "line5": "#eb8738", // Orangeish
            "line6": "#969594", // Distopian Grey
            "regularBus": "#da251d", // Red
            "streetcar": "#da251d", // Red
        };
        return fallbackColors[lineKey] || "#da251d";
    };

    // Helper function to get line display name
    const getLineDisplayName = (lineKey, message) => {
        console.log(">>>>>>>>>>>>>>>>>. ", lineKey);
        // For regularBus and streetcar, use affected_routes to get the route number
        if (lineKey === 'regularBus' || lineKey === 'streetcar') {
            if (message && message.affected_routes && message.affected_routes.length > 0) {
                const routeType = lineKey === 'streetcar' ? 'Streetcar' : 'Route';
                return `${routeType} ${message.affected_routes[0]}`;
            }
            return lineKey === 'streetcar' ? "Streetcar" : "Bus Route";
        }
        
        // If message has affected_routes, use the first route number as the display name
        if (message && message.affected_routes && message.affected_routes.length > 0) {
            const routeNum = message.affected_routes[0];
            // Route 9999 is a placeholder for general service alerts
            if (routeNum === 9999) {
                return "Service Alert";
            }
            // For subway lines (1-6), show the full line name
            if (routeNum >= 1 && routeNum <= 6) {
                if (window.stationData && window.stationData[lineKey] && window.stationData[lineKey].name) {
                    return window.stationData[lineKey].name;
                }
            }
            // For surface routes, show "Route XXX"
            return `Route ${routeNum}`;
        }
        // Fallback to stationData
        if (window.stationData && window.stationData[lineKey] && window.stationData[lineKey].name) {
            return window.stationData[lineKey].name;
        }
        // Fallback names
        const fallbackNames = {
            "line1University": "Line 1 University",
            "line1Yonge": "Line 1 Yonge",
            "line2": "Line 2",
            "line4": "Line 4"
        };
        return fallbackNames[lineKey] || lineKey;
    };

    // Collect line messages from window.alertData
    if (window.alertData) {
        // Track seen alerts to deduplicate (especially for Line 1 branches)
        const seenAlerts = new Set();

        Object.keys(window.alertData).forEach(lineKey => {
            const lineData = window.alertData[lineKey];

            if (window.alertData.site_wide_alerts) {
                window.alertData.site_wide_alerts.forEach(message => {
                    // Create a unique key for deduplication
                    const alertKey = `${message.text || message.headerText}|${message.type}|${message.start}`;
                    if (!seenAlerts.has(alertKey)) {
                        seenAlerts.add(alertKey);
                        lineMessages.push({
                            ...message,
                            lineKey: lineKey,
                            lineName: getLineDisplayName(lineKey, message),
                            lineColor: getLineColor(lineKey, message)
                        });
                    }
                });
            }

            // Check for line-level messages in alerts.line array
            if (lineData.alerts && lineData.alerts.line && Array.isArray(lineData.alerts.line)) {
                lineData.alerts.line.forEach(message => {
                    // Create a unique key for deduplication
                    const alertKey = `${message.text || message.headerText}|${message.type}|${message.start}`;
                    if (!seenAlerts.has(alertKey)) {
                        seenAlerts.add(alertKey);
                        lineMessages.push({
                            ...message,
                            lineKey: lineKey,
                            lineName: getLineDisplayName(lineKey, message),
                            lineColor: getLineColor(lineKey, message)
                        });
                    }
                });
            }

            // Handle regularBus alerts (route-based structure)
            if (lineKey === 'regularBus' && typeof lineData === 'object') {
                Object.keys(lineData).forEach(routeNumber => {
                    const routeData = lineData[routeNumber];
                    // Check if route has alerts array
                    if (routeData.route && Array.isArray(routeData.route)) {
                        routeData.route.forEach(message => {
                            // Create a unique key for deduplication
                            const alertKey = `${message.text || message.headerText}|${message.type}|${message.start}`;
                            if (!seenAlerts.has(alertKey)) {
                                seenAlerts.add(alertKey);
                                lineMessages.push({
                                    ...message,
                                    lineKey: lineKey,
                                    lineName: getLineDisplayName(lineKey, message),
                                    lineColor: getLineColor(lineKey, message)
                                });
                            }
                        });
                    }
                });
            }

            // Handle streetcar alerts (route-based structure)
            if (lineKey === 'streetcar' && typeof lineData === 'object') {
                Object.keys(lineData).forEach(routeNumber => {
                    const routeData = lineData[routeNumber];
                    // Check if route has alerts array
                    if (routeData.route && Array.isArray(routeData.route)) {
                        routeData.route.forEach(message => {
                            // Create a unique key for deduplication
                            const alertKey = `${message.text || message.headerText}|${message.type}|${message.start}`;
                            if (!seenAlerts.has(alertKey)) {
                                seenAlerts.add(alertKey);
                                lineMessages.push({
                                    ...message,
                                    lineKey: lineKey,
                                    lineName: getLineDisplayName(lineKey, message),
                                    lineColor: getLineColor(lineKey, message)
                                });
                            }
                        });
                    }
                });
            }
        });
    }

    console.log(`📢 Found ${lineMessages.length} line messages`);
    console.log('>>>>>>>>>>>>>>>>>>>>>>> Line messages:', lineMessages);
    console.log('window.alertData:', window.alertData);

    // Sort line messages by route number and alert type priority
    const getRouteNumber = (lineKey) => {
        const match = lineKey.match(/\d+/);
        return match ? parseInt(match[0], 10) : 999;
    };

    const getAlertTypePriority = (type) => {
        const priorities = {
            "NO_SERVICE": 0,
            "DETOUR": 1,
            "SIGNIFICANT_DELAYS": 2,
            "REDUCED_SERVICE": 3
        };
        return priorities[type] !== undefined ? priorities[type] : 999;
    };

    lineMessages.sort((a, b) => {
        const routeA = getRouteNumber(a.lineKey);
        const routeB = getRouteNumber(b.lineKey);

        // First sort by route number (1-6 first, then others in ascending order)
        if (routeA !== routeB) {
            // Prioritize lines 1-6
            const isLineA = routeA >= 1 && routeA <= 6;
            const isLineB = routeB >= 1 && routeB <= 6;
            if (isLineA && !isLineB) return -1;
            if (!isLineA && isLineB) return 1;
            return routeA - routeB;
        }

        // Then sort by alert type priority
        const priorityA = getAlertTypePriority(a.type);
        const priorityB = getAlertTypePriority(b.type);
        return priorityA - priorityB;
    });

    // Only show the section if there are line messages
    if (lineMessages.length === 0) {
        container.classList.add('hidden');
        return;
    }

    // Show the container
    container.classList.remove('hidden');

    // Generate the HTML content
    let content = `
        <div class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <button 
                onclick="toggleLineMessages()" 
                class="flex items-center justify-between w-full text-left focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 rounded"
                aria-expanded="false"
                aria-controls="line-messages-content">
                <div class="flex items-center gap-3">
                    ${iconFont('warning', 'text-yellow-600 dark:text-yellow-400 text-xl')}
                    <span class="font-semibold text-lg text-yellow-900 dark:text-yellow-100">Line Service Messages</span>
                    <span class="text-xs px-2 py-1 bg-yellow-200 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100 rounded-full font-medium">${lineMessages.length}</span>
                </div>
                <span id="line-messages-chevron" class="transition-transform duration-200">${iconFont('chevron-down', 'text-yellow-600 dark:text-yellow-400')}</span>
            </button>
            
            <div id="line-messages-content" class="hidden mt-4 space-y-3">
    `;

    // Process each line message
    lineMessages.forEach((message, index) => {
        // Determine message icon based on type
        let messageIcon = "info";
        if (message.type === "NO_SERVICE") {
            messageIcon = "x-circle";
        } else if (message.type === "REDUCED_SERVICE") {
            messageIcon = "warning";
        } else if (message.type === "ACCESSIBILITY_ISSUE") {
            messageIcon = "accessible";
        } else if (message.type === "CONSTRUCTION") {
            messageIcon = "construction";
        }

        const messageText = message.text || message.headerText || "Service message";

        // Format dates
        let dateInfo = "";
        if (message.start && message.start !== "0001-01-01T00:00:00Z") {
            try {
                const startDate = new Date(message.start).toLocaleDateString();
                if (message.end && message.end !== "0001-01-01T00:00:00Z") {
                    const endDate = new Date(message.end).toLocaleDateString();
                    dateInfo = `<div class="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        ${startDate} - ${endDate}
                    </div>`;
                } else {
                    dateInfo = `<div class="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        Since: ${startDate}
                    </div>`;
                }
            } catch (e) {
                // If date parsing fails, show raw dates
                if (message.start && message.end) {
                    dateInfo = `<div class="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                        ${message.start} - ${message.end}
                    </div>`;
                }
            }
        }

        // Affected stations info
        let stationsInfo = "";
        if (message.stopNameList && message.stopNameList.length > 0) {
            const stations = message.stopNameList.filter(station => station && station.trim() !== "");
            if (stations.length > 0) {
                stationsInfo = `<div class="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    <span class="font-medium">Affected stations:</span> ${stations.join(', ')}
                </div>`;
            }
        }

        // Severity badge
        let severityBadge = "";
        if (message.severity) {
            const severityColors = {
                "CRITICAL": "bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200",
                "WARNING": "bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200",
                "NOTICE": "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
            };
            const colorClass = severityColors[message.severity] || "bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200";
            severityBadge = `<span class="text-xs px-2 py-1 ${colorClass} rounded uppercase font-medium">
                ${message.severity}
            </span>`;
        }

        content += `
            <div class="bg-white dark:bg-yellow-900/30 border-t border-r border-b border-yellow-200 dark:border-yellow-700 rounded-lg p-3" style="border-left: 3px solid ${message.lineColor} !important;">
                <div class="flex items-start gap-3">
                    <div class="flex-shrink-0 mt-0.5">
                        ${iconFont(messageIcon, 'text-yellow-600 dark:text-yellow-400')}
                    </div>
                    <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-2 flex-wrap">
                            <div class="w-3 h-3 rounded-full" style="background-color: ${message.lineColor}"></div>
                            <span class="font-medium text-yellow-900 dark:text-yellow-100 text-sm">${message.lineName}</span>
                            <span class="text-xs px-2 py-1 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200 rounded uppercase font-medium">
                                ${message.type ? message.type.replace('_', ' ') : 'UNKNOWN'}
                            </span>
                        </div>
                        <p class="text-yellow-800 dark:text-yellow-200 text-sm leading-relaxed">${messageText}</p>
                        ${dateInfo}
                        ${stationsInfo}
                        ${message.url ? `<div class="mt-2">
                            <a href="${message.url}" target="_blank" rel="noopener noreferrer" 
                               class="text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:hover:text-yellow-200 text-xs underline">
                                More details →
                            </a>
                        </div>` : ''}
                    </div>
                </div>
            </div>
        `;
    });

    content += `
            </div>
        </div>
    `;

    container.innerHTML = content;
}

/**
 * Toggle line messages section visibility
 */
function toggleLineMessages() {
    const content = document.getElementById('line-messages-content');
    const chevron = document.getElementById('line-messages-chevron');
    const button = content.previousElementSibling;

    if (content.classList.contains('hidden')) {
        // Show content
        content.classList.remove('hidden');
        chevron.style.transform = 'rotate(180deg)';
        button.setAttribute('aria-expanded', 'true');
    } else {
        // Hide content
        content.classList.add('hidden');
        chevron.style.transform = 'rotate(0deg)';
        button.setAttribute('aria-expanded', 'false');
    }
}

// Export functions
export {
    generateLineMessages,
    toggleLineMessages
};

// Make functions available globally for onclick handlers
window.generateLineMessages = generateLineMessages;
window.toggleLineMessages = toggleLineMessages;